import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AuthenticationActions } from '@/app/slices';
import { clearAuthTokens, setAuthTokens } from '@/lib/tokenStorage';
import {
  getNucleusAccessErrorMessage,
  hasNucleusAccess,
} from '@/services/auth/util';
import { authFetch } from '@/services/auth/authService';

const LAST_USED_DB_KEY = 'tog_last_used_database';

function decodeJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
}

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);

        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const expiresIn = params.get('expires_in');
        const module = params.get('module');

        console.log('Parsing tokens from URL hash:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          expiresIn,
        });

        if (!accessToken || !refreshToken) {
          console.error('Missing tokens in URL hash');
          setError('Invalid authentication response - no tokens found');
          return;
        }

        setAuthTokens({
          accessToken,
          refreshToken,
          expiresIn: expiresIn ? parseInt(expiresIn) : 3600,
        });

        console.log('Tokens stored, fetching user data...');

        const res = await authFetch('/api/auth/me', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          console.error('Failed to fetch user data');
          clearAuthTokens();
          setError('Failed to load user profile. Please try again.');
          return;
        }

        const currentUser = await res.json();

        if (!hasNucleusAccess(currentUser)) {
          const errorMessage = getNucleusAccessErrorMessage(currentUser);
          console.error(
            'User does not have permission to access Nucleus:',
            errorMessage,
          );
          clearAuthTokens();
          setError(errorMessage);
          return;
        }

        console.log('User data loaded, updating Redux state...');

        dispatch({
          type: AuthenticationActions.UPDATE_ME,
          me: {
            userId: currentUser.userId,
            azureObjectId: currentUser.azureObjectId,
            email: currentUser.email,
            displayName: currentUser.displayName,
            givenName: currentUser.givenName,
            surname: currentUser.surname,
            jobTitle: currentUser.jobTitle,
            department: currentUser.department,
            companyName: currentUser.companyName,
            mobilePhone: currentUser.mobilePhone,
            preferredLanguage: currentUser.preferredLanguage,
            accountEnabled: currentUser.accountEnabled,
            roles: currentUser.roles || [],
            companies: currentUser.companies || [],
            permissions: currentUser.permissions || [],
            sites: currentUser.sites || [],
          },
        });

        dispatch({
          type: AuthenticationActions.UPDATE_IS_CSR,
          payload: currentUser.roles?.some((role: any) =>
            role.roleId.toLowerCase().includes('csr'),
          ),
        });

        const decoded = decodeJwt(accessToken);
        const canSwitchDatabase =
          decoded?.can_switch_database === 'true' ||
          decoded?.can_switch_database === true;
        const currentCompanyId = decoded?.active_company_id;
        const lastUsedDb = localStorage.getItem(LAST_USED_DB_KEY);

        if (
          canSwitchDatabase &&
          lastUsedDb &&
          currentCompanyId &&
          lastUsedDb !== currentCompanyId &&
          currentUser.companies?.some((c: any) => c.companyId === lastUsedDb)
        ) {
          console.log('Switching to last used database:', lastUsedDb);
          try {
            const switchRes = await authFetch(
              '/api/auth/switch-company',
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ companyId: lastUsedDb }),
              },
            );

            if (switchRes.ok) {
              const switchData = await switchRes.json();
              setAuthTokens({
                accessToken: switchData.accessToken,
                refreshToken: switchData.refreshToken,
                expiresIn: switchData.expiresIn || 3600,
              });
              console.log('Database switched successfully');
            }
          } catch (switchError) {
            console.error(
              'Failed to switch to last used database:',
              switchError,
            );
          }
        }

        console.log('Authentication complete, navigating to home...');

        navigate(module ? `/${module}` : '/', { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Authentication failed. Please try again.');
      }
    };

    handleCallback();
  }, [navigate, dispatch]);

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: '1.5rem',
        }}
      >
        <div
          style={{
            fontSize: '3rem',
            color: '#ef4444',
          }}
        >
          ⚠
        </div>
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            margin: '0',
            color: '#1f2937',
          }}
        >
          Authentication Error
        </h2>
        <p
          style={{
            fontSize: '1rem',
            color: '#6b7280',
            maxWidth: '400px',
            textAlign: 'center',
            margin: '0',
          }}
        >
          {error}
        </p>
        <button
          onClick={() => navigate('/login')}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            color: 'white',
            backgroundColor: '#5e81ac',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          Return to Login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '2rem',
      }}
    >
      <div
        style={{
          width: '64px',
          height: '64px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #5e81ac',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}
      ></div>
      <div style={{ textAlign: 'center' }}>
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            margin: '0 0 0.5rem 0',
            color: '#1f2937',
          }}
        >
          Completing authentication
        </h2>
        <p
          style={{
            fontSize: '1rem',
            color: '#6b7280',
            margin: '0',
          }}
        >
          Please wait while we log you in...
        </p>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
